import type { ReactNode } from 'react';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import {
    MetaLinksContext,
    useMetaLinkContext,
} from '../../contexts/MetaLinksContext';
import { MetaLink } from '../MetaLink';
import { getCurrentURL } from '../../util/getCurrentURL';
import { getMetaLinksFromAvailableLanguages } from '../../util/getMetaLinksFromAvailableLanguages';
import useStoreAndLanguageParams from '../../hooks/useStoreAndLanguageParams';
import { StoreContext } from '../../contexts/StoreContext';

interface MetaLinkProviderProps {
    children: ReactNode;
}

const MetaLinkProvider = ({ children }: MetaLinkProviderProps) => {
    const { language, availableLanguages } = useStoreAndLanguageParams();
    const { store } = useContext(StoreContext);

    const [link, setLink] = useState('');
    const contextValue = useMemo(
        () => ({
            setLink,
        }),
        []
    );

    const currentURL = link || getCurrentURL();

    const metaLinks = getMetaLinksFromAvailableLanguages({
        availableLanguages,
        currentLanguage: language,
        storeUrl: store?.url,
        pathname: new URL(currentURL).pathname,
    });

    return (
        <MetaLinksContext.Provider value={contextValue}>
            <MetaLink rel="canonical" href={currentURL} />
            {metaLinks.map(({ language: lang, href }) => (
                <MetaLink
                    key={lang}
                    rel="alternate"
                    hreflang={lang}
                    href={href}
                />
            ))}
            {children}
        </MetaLinksContext.Provider>
    );
};

export const useSetCanonicalLink = (link: string) => {
    const { setLink } = useMetaLinkContext();

    useEffect(() => {
        setLink(link);

        return () => {
            setLink('');
        };
    }, [link, setLink]);

    return null;
};

export default memo(MetaLinkProvider);
